import React from 'react';
import { useStringsContext } from '../../../../../contexts/strings';
import SquaDialog, { SquadDialogProps } from '../squad-dialog';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { SquadPlayer } from '../../types';
import { usePlayer } from '../../hooks/player';
import useTeamData from '../../../../../hooks/useTeamData';

interface Props extends Pick<SquadDialogProps, 'open' | 'onClose'> {
  player: SquadPlayer;
  teamId: string;
  season: number;
  onRemovedPlayer: (player: SquadPlayer) => void;
}

export default function RemovePlayerModal({
  open,
  teamId,
  season,
  onClose,
  onRemovedPlayer,
  player,
}: Props) {
  const { strings } = useStringsContext();
  const { removePlayer } = usePlayer();
  const [loading, setLoading] = React.useState(false);

  const team = useTeamData({ teamAbbrv: teamId });

  const onDelete = async () => {
    setLoading(true);

    try {
      await removePlayer(teamId, season, player);
      onRemovedPlayer(player);
      if (onClose) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  if (team.query.isLoading) {
    return null;
  }

  return (
    <SquaDialog
      open={open}
      onClose={onClose}
      title={strings.squad.removePlayerFromSquad
        .replace('$name', player.displayName || player.fullName)
        .replace('$season', season.toString())
        .replace('$team', team.query.data?.name || teamId)}
    >
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.squad.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={loading}
          startIcon={<DeleteIcon />}
          onClick={onDelete}
        >
          {strings.squad.yesRemove}
        </LoadingButton>
      </DialogActions>
    </SquaDialog>
  );
}
